import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, ArrowLeft, RotateCcw, Send, CheckCircle2, Wallet } from "lucide-react";
import { api } from "@/lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/sptl1rmt_image%201.png";

// Persistent session id
function getSessionId() {
  let id = localStorage.getItem("dd_chat_session");
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("dd_chat_session", id);
  }
  return id;
}

const PRODUCTS = {
  milk: { key: "milk", label: "🥛 A2 GIR Cow Milk", price: "₹100/litre" },
  ghee: { key: "ghee", label: "🧈 Pure Cow Ghee", price: "Price on Call" },
  corporate: { key: "corporate", label: "🏢 Corporate / Bulk 5L+", price: "Call for pricing" },
};

// Stable product-key based detection — avoids label-text coupling
function isGheeProduct(data) {
  return data?.product_key === "ghee";
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("MAIN");
  const [history, setHistory] = useState([]);        // stack of steps for "Back"
  const [data, setData] = useState({});              // booking / support form
  const [loading, setLoading] = useState(false);
  const [balanceResult, setBalanceResult] = useState(null);
  const [doneInfo, setDoneInfo] = useState(null);
  const sessionId = useRef(getSessionId()).current;

  const goto = (next) => {
    setHistory((h) => [...h, step]);
    setStep(next);
  };
  const back = () => {
    setHistory((h) => {
      if (h.length === 0) { setStep("MAIN"); return []; }
      const prev = h[h.length - 1];
      setStep(prev);
      return h.slice(0, -1);
    });
  };
  const reset = () => {
    setHistory([]);
    setStep("MAIN");
    setData({});
    setBalanceResult(null);
    setDoneInfo(null);
  };

  const submitBooking = async () => {
    // Validate: name non-empty + phone 10 digits
    const phoneDigits = (data.phone || "").replace(/\D/g, "");
    const phone10 = phoneDigits.length === 10 ? phoneDigits : phoneDigits.length >= 10 ? phoneDigits.slice(-10) : "";
    if (!data.name?.trim() || !phone10) {
      alert("Please enter a valid name and 10-digit phone number.");
      return;
    }
    const isGhee = isGheeProduct(data);
    setLoading(true);
    try {
      await api.post("/chatbot/booking", {
        session_id: sessionId,
        product: data.product,
        quantity: data.quantity,
        delivery_type: isGhee ? "N/A (Ghee enquiry)" : data.delivery_type,
        address: data.address || "",
        name: data.name,
        phone: `+91${phone10}`,
      });
      // Open WhatsApp with pre-filled booking details (different template for Ghee vs Milk)
      let msg;
      if (isGhee) {
        msg =
          `Namaste! Ghee enquiry:\n` +
          `Naam: ${data.name}\n` +
          `Phone: +91${phone10}\n` +
          `Ghee Qty: ${data.quantity}\n` +
          (data.address ? `Address: ${data.address}\n` : "") +
          `Price confirm karein 🙏`;
      } else {
        msg =
          `Namaste! Meri booking:\n` +
          `Naam: ${data.name}\n` +
          `Phone: +91${phone10}\n` +
          `Product: ${data.product}\n` +
          `Qty: ${data.quantity}\n` +
          `Delivery: ${data.delivery_type}\n` +
          `Address: ${data.address}\n` +
          `Confirm karein 🙏`;
      }
      window.open(`https://wa.me/919819933676?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
      setDoneInfo({ ...data, phone: `+91${phone10}`, isGhee });
      setStep("BOOKING_DONE");
    } catch {
      alert("Booking save failed. Please WhatsApp us directly at +91 98199 33676");
    } finally {
      setLoading(false);
    }
  };

  const submitAction = async (action) => {
    setLoading(true);
    try {
      await api.post("/chatbot/action", {
        session_id: sessionId, action, name: data.name || null, phone: data.phone || null,
      });
      setStep(action === "PAUSED" ? "PAUSE_DONE" : "RESUME_DONE");
    } finally { setLoading(false); }
  };

  const submitSupport = async (issue_type) => {
    setLoading(true);
    try {
      await api.post("/chatbot/support", {
        session_id: sessionId, issue_type, name: data.name || null, phone: data.phone || null,
      });
      setStep("SUPPORT_DONE");
    } finally { setLoading(false); }
  };

  const fetchBalance = async (phone) => {
    setLoading(true);
    try {
      const res = await api.post("/chatbot/balance", { phone });
      setBalanceResult(res.data);
      setStep("BALANCE_RESULT");
    } finally { setLoading(false); }
  };

  // Auto-reset when closed so next open starts fresh
  useEffect(() => { if (!open) setTimeout(reset, 300); /* eslint-disable-next-line */ }, [open]);

  return (
    <>
      {/* Toggle — BOTTOM LEFT */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 left-5 md:bottom-8 md:left-8 z-50 flex items-center gap-2 rounded-full bg-gradient-to-br from-[#1a3a2a] to-[#0A1F15] text-dd-cream pl-4 pr-5 py-3 shadow-xl border border-[#c9a227]/50 hover:from-[#235040] hover:to-[#1a3a2a] transition-all"
        aria-label={open ? "Close chat" : "Open chat"}
        data-testid="chatbot-toggle"
      >
        <span className="w-7 h-7 rounded-full bg-[#c9a227] text-[#1a3a2a] flex items-center justify-center text-sm font-bold">
          {open ? <X className="w-4 h-4" strokeWidth={2.5} /> : "💬"}
        </span>
        <span className="hidden sm:inline font-semibold text-sm tracking-wide">{open ? "Close" : "Chat with us"}</span>
      </button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="fixed bottom-24 left-5 md:left-8 z-50 w-[calc(100vw-2.5rem)] sm:w-[380px] max-w-[380px] h-[500px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl border border-[#c9a227]/40 overflow-hidden flex flex-col"
            data-testid="chatbot-panel"
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-[#1a3a2a] to-[#0A1F15] text-dd-cream px-4 py-3 flex items-center gap-3 border-b border-[#c9a227]/30">
              <img src={LOGO} alt="logo" className="w-10 h-10 rounded-full bg-white p-0.5" />
              <div className="flex-1">
                <div className="font-serif italic text-lg text-[#e6c97a] leading-none">Divya Dhara</div>
                <div className="text-[0.68rem] text-[#C9BFAE] mt-1 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> Live support
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#C9BFAE] hover:text-[#c9a227]" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body — single step at a time */}
            <div className="flex-1 overflow-y-auto bg-[#FAF7F2] px-4 py-4">
              <StepRenderer
                step={step}
                data={data}
                setData={setData}
                goto={goto}
                loading={loading}
                balanceResult={balanceResult}
                doneInfo={doneInfo}
                submitBooking={submitBooking}
                submitAction={submitAction}
                submitSupport={submitSupport}
                fetchBalance={fetchBalance}
              />
            </div>

            {/* Footer — Back + Restart (hidden on MAIN and DONE screens) */}
            {step !== "MAIN" && !["BOOKING_DONE", "PAUSE_DONE", "RESUME_DONE", "SUPPORT_DONE"].includes(step) && (
              <div className="bg-white border-t border-dd-line px-3 py-2.5 flex items-center justify-between gap-2">
                <button
                  onClick={back}
                  className="flex items-center gap-1.5 text-xs text-dd-ink-muted hover:text-dd-ink font-medium px-3 py-1.5 rounded-full border border-dd-line hover:border-[#c9a227]"
                  data-testid="chatbot-back"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Wapas Jao
                </button>
                <button
                  onClick={reset}
                  className="flex items-center gap-1.5 text-xs text-[#8F6E28] hover:text-[#c9a227] font-medium px-3 py-1.5 rounded-full bg-[#c9a227]/10"
                  data-testid="chatbot-restart"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Naya Start
                </button>
              </div>
            )}
            {["BOOKING_DONE", "PAUSE_DONE", "RESUME_DONE", "SUPPORT_DONE"].includes(step) && (
              <div className="bg-white border-t border-dd-line px-3 py-2.5">
                <button
                  onClick={reset}
                  className="w-full flex items-center justify-center gap-1.5 text-sm text-[#1a3a2a] bg-[#c9a227] hover:bg-[#b08f1e] font-bold px-3 py-2 rounded-full"
                  data-testid="chatbot-newchat"
                >
                  <RotateCcw className="w-4 h-4" /> 🔁 Nayi Booking / Main Menu
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ---------------- Step renderer ----------------

function BotMessage({ children }) {
  return (
    <div className="bg-white border border-dd-line rounded-2xl rounded-bl-sm p-3.5 mb-3 text-sm text-dd-ink leading-relaxed whitespace-pre-wrap">
      {children}
    </div>
  );
}

function OptionButton({ onClick, children, testid, tone = "default" }) {
  const tones = {
    default: "bg-white border-2 border-[#c9a227]/40 hover:border-[#c9a227] hover:bg-[#c9a227]/10 text-dd-ink",
    gold: "bg-gradient-to-b from-[#c9a227] to-[#8F6E28] hover:from-[#e6c97a] hover:to-[#c9a227] text-[#1a3a2a] font-bold border-0",
    green: "bg-[#1a3a2a] hover:bg-[#235040] text-dd-cream border-0",
    danger: "bg-red-50 border-2 border-red-200 hover:border-red-400 text-red-700",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${tones[tone]} shadow-sm hover:shadow-md`}
    >
      {children}
    </button>
  );
}

function TextField({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div className="mb-2.5">
      {label && <label className="block text-xs font-semibold text-dd-ink-muted mb-1">{label}</label>}
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white border border-dd-line rounded-xl px-3 py-2.5 text-sm text-dd-ink focus:border-[#c9a227] focus:ring-1 focus:ring-[#c9a227] outline-none"
        data-testid={testid}
      />
    </div>
  );
}

function StepRenderer({ step, data, setData, goto, loading, balanceResult, doneInfo, submitBooking, submitAction, submitSupport, fetchBalance }) {
  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // ---------- MAIN MENU ----------
  if (step === "MAIN") return (
    <>
      <BotMessage>Namaste! 🙏 Divya Dhara mein aapka swagat hai 🥛{"\n\n"}Aap kya karna chahte hain?</BotMessage>
      <div className="space-y-2" data-testid="main-menu">
        <OptionButton tone="gold" onClick={() => goto("PRODUCT")} testid="menu-start">1️⃣ Milk Delivery Start Kare</OptionButton>
        <OptionButton onClick={() => goto("PAUSE_CONFIRM")} testid="menu-pause">2️⃣ Delivery Pause Kare</OptionButton>
        <OptionButton onClick={() => goto("RESUME_CONFIRM")} testid="menu-resume">3️⃣ Delivery Resume Kare</OptionButton>
        <OptionButton onClick={() => goto("BALANCE_PHONE")} testid="menu-balance">4️⃣ Wallet Balance Check Kare</OptionButton>
        <OptionButton onClick={() => goto("SUPPORT")} testid="menu-support">5️⃣ Support Chahiye</OptionButton>
      </div>
    </>
  );

  // ---------- NEW BOOKING FLOW ----------
  if (step === "PRODUCT") return (
    <>
      <BotMessage>Kya lena chahoge?</BotMessage>
      <div className="space-y-2" data-testid="step-product">
        <OptionButton tone="gold" onClick={() => { upd("product", PRODUCTS.milk.label); upd("product_key", PRODUCTS.milk.key); goto("QTY"); }} testid="product-milk">
          🥛 A2 GIR Cow Milk · ₹100/litre
        </OptionButton>
        <OptionButton tone="gold" onClick={() => { upd("product", PRODUCTS.ghee.label); upd("product_key", PRODUCTS.ghee.key); goto("QTY"); }} testid="product-ghee">
          🧈 Pure Cow Ghee · Price on Call
        </OptionButton>
        <OptionButton tone="green" onClick={() => { upd("product", PRODUCTS.corporate.label); upd("product_key", PRODUCTS.corporate.key); goto("CORPORATE"); }} testid="product-corporate">
          🏢 Corporate / Bulk 5L+
        </OptionButton>
      </div>
    </>
  );

  if (step === "CORPORATE") return (
    <div className="text-center" data-testid="step-corporate">
      <div className="text-4xl mb-3">🏢</div>
      <BotMessage>Corporate aur Bulk orders ke liye seedha call karein — hamari team aapke liye custom plan banayegi (5L+ daily, GST invoice, dedicated manager).</BotMessage>
      <a href="tel:+919819933676" className="inline-flex items-center gap-2 bg-gradient-to-b from-dd-gold to-[#8F6E28] text-dd-ink-dark px-6 py-3 rounded-full font-bold" data-testid="corporate-call">
        📞 Call: +91 98199 33676
      </a>
    </div>
  );

  if (step === "QTY") {
    const isGhee = isGheeProduct(data);
    // For Ghee: skip DELIVERY and ADDRESS — go directly to NAME+PHONE per user spec
    const next = isGhee ? "NAME_PHONE" : "DELIVERY";
    return (
      <>
        <BotMessage>Kitni quantity chahiye?{"\n"}(Example: 1 litre daily, 2 kg)</BotMessage>
        <TextField value={data.quantity} onChange={(v) => upd("quantity", v)} placeholder="e.g. 1 litre daily" testid="step-qty-input" />
        <OptionButton tone="gold" onClick={() => data.quantity?.trim() && goto(next)} testid="step-qty-next">
          Aage Jao →
        </OptionButton>
      </>
    );
  }

  if (step === "DELIVERY") return (
    <>
      <BotMessage>Delivery kaise chahiye?</BotMessage>
      <div className="space-y-2" data-testid="step-delivery">
        <OptionButton tone="gold" onClick={() => { upd("delivery_type", "Daily"); goto("ADDRESS"); }} testid="delivery-daily">
          📅 Daily — Roz
        </OptionButton>
        <OptionButton tone="gold" onClick={() => { upd("delivery_type", "Alternate Day"); goto("ADDRESS"); }} testid="delivery-alt">
          📅 Alternate Day — Ek din chod ke
        </OptionButton>
      </div>
    </>
  );

  if (step === "ADDRESS") return (
    <>
      <BotMessage>Apna full address type karo 📍{"\n"}(Society, Sector, Area, City, Pincode)</BotMessage>
      <TextField value={data.address} onChange={(v) => upd("address", v)} placeholder="Flat / Society / Sector / Area / Pincode" testid="step-address-input" />
      <OptionButton tone="gold" onClick={() => data.address?.trim()?.length > 8 && goto("NAME_PHONE")} testid="step-address-next">
        Aage Jao →
      </OptionButton>
    </>
  );

  if (step === "NAME_PHONE") return (
    <>
      <BotMessage>Last step! 😊{"\n"}Aapka naam aur phone number?</BotMessage>
      <TextField label="Naam" value={data.name} onChange={(v) => upd("name", v)} placeholder="Apna naam" testid="step-name-input" />
      <TextField label="Phone Number" type="tel" value={data.phone} onChange={(v) => upd("phone", v)} placeholder="+91 98XXXXXXXX" testid="step-phone-input" />
      <OptionButton tone="gold" onClick={() => data.name?.trim() && data.phone?.trim() && !loading && submitBooking()} testid="step-confirm">
        {loading ? "Saving…" : "✅ Booking Confirm Karo"}
      </OptionButton>
    </>
  );

  if (step === "BOOKING_DONE") {
    if (isGheeProduct(doneInfo)) {
      return (
        <div data-testid="booking-done">
          <div className="text-center mb-3">
            <CheckCircle2 className="w-14 h-14 mx-auto text-green-600" strokeWidth={1.5} />
          </div>
          <BotMessage>
            ✅ Ghee Enquiry Ho Gayi! 🎉{"\n"}
            ━━━━━━━━━━━━━━━{"\n"}
            👤 Naam: {doneInfo?.name}{"\n"}
            📱 Phone: {doneInfo?.phone}{"\n"}
            🧈 Product: Pure Bilona Ghee{"\n"}
            📦 Quantity: {doneInfo?.quantity}{"\n"}
            {doneInfo?.address ? `📍 Address: ${doneInfo?.address}\n` : ""}
            ━━━━━━━━━━━━━━━{"\n"}
            Hamari team aapko call karke price aur delivery confirm karegi. 🙏
          </BotMessage>
        </div>
      );
    }
    return (
      <div data-testid="booking-done">
        <div className="text-center mb-3">
          <CheckCircle2 className="w-14 h-14 mx-auto text-green-600" strokeWidth={1.5} />
        </div>
        <BotMessage>
          ✅ Booking ho gayi! Shukriya! 🙏{"\n"}
          ━━━━━━━━━━━━━━━━━━{"\n"}
          🥛 Product: {doneInfo?.product}{"\n"}
          📦 Quantity: {doneInfo?.quantity}{"\n"}
          🚚 Delivery: {doneInfo?.delivery_type}{"\n"}
          📍 Address: {doneInfo?.address}{"\n"}
          👤 Naam: {doneInfo?.name}{"\n"}
          📱 Phone: {doneInfo?.phone}{"\n"}
          ━━━━━━━━━━━━━━━━━━{"\n"}
          Hamari team 2 ghante mein aapse WhatsApp par contact karegi. 🙏
        </BotMessage>
      </div>
    );
  }

  // ---------- PAUSE FLOW ----------
  if (step === "PAUSE_CONFIRM") return (
    <>
      <BotMessage>Delivery pause karna chahte ho?</BotMessage>
      <div className="space-y-2" data-testid="step-pause">
        <TextField label="Phone Number (optional)" type="tel" value={data.phone} onChange={(v) => upd("phone", v)} placeholder="+91 98XXXXXXXX" testid="pause-phone" />
        <OptionButton tone="gold" onClick={() => !loading && submitAction("PAUSED")} testid="pause-yes">⏸️ Haan, Pause Karo</OptionButton>
        <OptionButton onClick={() => goto("MAIN")} testid="pause-no">↩️ Nahi, Wapas Jao</OptionButton>
      </div>
    </>
  );

  if (step === "PAUSE_DONE") return (
    <BotMessage>⏸️ Aapki delivery pause ho gayi.{"\n"}Resume karna ho to wapas aana. 🙏</BotMessage>
  );

  // ---------- RESUME FLOW ----------
  if (step === "RESUME_CONFIRM") return (
    <>
      <BotMessage>Delivery resume karna chahte ho?</BotMessage>
      <div className="space-y-2" data-testid="step-resume">
        <TextField label="Phone Number (optional)" type="tel" value={data.phone} onChange={(v) => upd("phone", v)} placeholder="+91 98XXXXXXXX" testid="resume-phone" />
        <OptionButton tone="gold" onClick={() => !loading && submitAction("RESUMED")} testid="resume-yes">▶️ Haan, Resume Karo</OptionButton>
        <OptionButton onClick={() => goto("MAIN")} testid="resume-no">↩️ Nahi, Wapas Jao</OptionButton>
      </div>
    </>
  );

  if (step === "RESUME_DONE") return (
    <BotMessage>▶️ Kal se aapki delivery start ho jayegi! 🥛{"\n"}Shukriya Divya Dhara choose karne ke liye 🙏</BotMessage>
  );

  // ---------- BALANCE FLOW ----------
  if (step === "BALANCE_PHONE") return (
    <>
      <BotMessage>💰 Wallet balance dekhne ke liye apna phone number type karo</BotMessage>
      <TextField type="tel" value={data.phone} onChange={(v) => upd("phone", v)} placeholder="+91 98XXXXXXXX" testid="balance-phone" />
      <OptionButton tone="gold" onClick={() => data.phone?.trim() && !loading && fetchBalance(data.phone)} testid="balance-fetch">
        {loading ? "Checking…" : "💰 Check Balance"}
      </OptionButton>
    </>
  );

  if (step === "BALANCE_RESULT") {
    if (!balanceResult?.found) return (
      <>
        <BotMessage>😔 Is phone number pe koi account nahi mila.{"\n\n"}Pehle ek booking karke dekhein? 🙏</BotMessage>
        <OptionButton tone="gold" onClick={() => goto("PRODUCT")} testid="balance-to-book">🥛 Milk Delivery Start Kare</OptionButton>
      </>
    );
    return (
      <>
        <div className="text-center mb-3">
          <Wallet className="w-10 h-10 mx-auto text-[#c9a227]" strokeWidth={1.5} />
        </div>
        <BotMessage>
          💰 Aapka Wallet Balance:{"\n"}
          ━━━━━━━━━━━━━━━━━━{"\n"}
          Balance: ₹{balanceResult.balance}{"\n"}
          Daily Deduction: ₹{balanceResult.daily_rate}/litre{"\n"}
          Approx Days Left: {balanceResult.days_left}{"\n"}
          ━━━━━━━━━━━━━━━━━━{"\n"}
          Recharge karne ke liye neeche button dabao 👇
        </BotMessage>
        <OptionButton tone="gold" onClick={() => window.open("https://wa.me/919819933676?text=Namaste!%20Main%20apna%20Divya%20Dhara%20wallet%20recharge%20karna%20chahta%20hun.", "_blank")} testid="balance-recharge">
          💳 Wallet Recharge Karo
        </OptionButton>
      </>
    );
  }

  // ---------- SUPPORT FLOW ----------
  if (step === "SUPPORT") return (
    <>
      <BotMessage>Kya problem hai? 🤔</BotMessage>
      <TextField label="Naam (optional)" value={data.name} onChange={(v) => upd("name", v)} placeholder="Apna naam" testid="support-name" />
      <TextField label="Phone (optional)" type="tel" value={data.phone} onChange={(v) => upd("phone", v)} placeholder="+91 98XXXXXXXX" testid="support-phone" />
      <div className="space-y-2 mt-2" data-testid="step-support">
        <OptionButton onClick={() => !loading && submitSupport("Delivery nahi aayi")} testid="support-delivery">🚚 Delivery nahi aayi</OptionButton>
        <OptionButton onClick={() => !loading && submitSupport("Quality issue")} testid="support-quality">⚠️ Quality issue hai</OptionButton>
        <OptionButton onClick={() => !loading && submitSupport("Payment/Billing issue")} testid="support-payment">💰 Payment / Billing issue</OptionButton>
        <OptionButton onClick={() => !loading && submitSupport("Other")} testid="support-other">❓ Kuch aur</OptionButton>
      </div>
    </>
  );

  if (step === "SUPPORT_DONE") return (
    <BotMessage>✅ Aapki problem note ho gayi.{"\n"}Hamari team jald hi aapse WhatsApp par contact karegi. 🙏</BotMessage>
  );

  return <BotMessage>Loading…</BotMessage>;
}
